/**
 * TSR Sales Report → the editorial PDF's data, built in ONE place for both doors.
 *
 * The Export modal assembled this object inline; Share screenshotted the page
 * instead, so the two produced different documents. Both now fetch the same
 * /reports/enhanced-export answer (salesEnhancedExportQuery) and hand it here.
 */
import { profitSplit } from './salesReportHelpers';

const toYMD = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    const d = (v instanceof Date) ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Which kind of period is on screen — decides which sections the report can carry. */
export function salesPeriodFlags({ selectedPeriod, selectedDate, selectedWeek } = {}) {
    const hasSalesTrend = !(selectedWeek && selectedWeek.isCustomRange)
        && !(selectedPeriod === 'Today' && !selectedDate && !selectedWeek);
    const isSingleDaySelection =
        (selectedPeriod === 'Today' && !selectedDate && !selectedWeek) ||
        Boolean(selectedWeek && selectedWeek.isCustomRange && selectedWeek.isSingleDay) ||
        Boolean(selectedDate && !selectedDate.isMonthSelection);
    // A custom multi-day date range (Range mode). Gets a Date-wise Breakdown table
    // instead of the fixed Sales Comparison Chart.
    const isCustomMultiDayRange = Boolean(selectedWeek && selectedWeek.isCustomRange && !selectedWeek.isSingleDay);
    const hasProfitCogs = Boolean(selectedPeriod === 'Today' || selectedDate || selectedWeek ||
        selectedPeriod === 'This Week' || selectedPeriod === 'This Month' || selectedPeriod === 'This Year');
    return { hasSalesTrend, isSingleDaySelection, isCustomMultiDayRange, hasProfitCogs };
}

/** The sections the report offers for this period (individual summary items first). */
export function salesPdfSections({ selectedPeriod, selectedDate, selectedWeek, topProfitItems = [], dailyBreakdown = [], outletRows = [], fixedShare = null } = {}) {
    const { hasSalesTrend, isSingleDaySelection, isCustomMultiDayRange, hasProfitCogs } =
        salesPeriodFlags({ selectedPeriod, selectedDate, selectedWeek });
    const hasTopItems = (topProfitItems || []).length > 0 && isSingleDaySelection;
    const hasDateBreakdown = (dailyBreakdown || []).length > 0 && isCustomMultiDayRange;
    const hasOutlets = (outletRows || []).some((r) => r && r.outlet);
    const hasFixed = !!(fixedShare && fixedShare.available && (fixedShare.groups || []).length > 0);
    return [
        { id: 'total-sales',       label: 'Total Sales',                      group: 'summary' },
        { id: 'expenses',          label: 'Expenses',                         group: 'summary' },
        { id: 'gross-profit',      label: 'Gross Profit',                     group: 'summary' },
        { id: 'weekly-monthly-yearly-summary', label: 'Weekly/Monthly/Yearly Summary', group: 'summary' },
        { id: 'items-sold-breakdown', label: 'Items Sold Detailed Breakdown', group: 'table' },
        { id: 'stock-in-details',  label: 'Stock-In Details (Ingredient Purchases)', group: 'table' },
        { id: 'stock-out-details', label: 'Stock-Out Details (Ingredient Usage)',   group: 'table' },
        { id: 'non-chargeable-expenses', label: 'Non-Chargeable Expenses',          group: 'table' },
        ...(hasFixed         ? [{ id: 'fixed-expenses', label: 'Fixed Expenses (Salaries, Rent, Bills)', group: 'table' }] : []),
        ...(hasSalesTrend    ? [{ id: 'sales-trend',    label: 'Sales Comparison Chart',           group: 'chart' }] : []),
        ...(hasDateBreakdown ? [{ id: 'date-breakdown', label: 'Date-wise Breakdown',              group: 'table' }] : []),
        ...(hasTopItems      ? [{ id: 'top-items',      label: 'Top 5 Items by Gross Profit',      group: 'chart' }] : []),
        ...(hasProfitCogs    ? [{ id: 'profit-cogs',    label: 'Gross Profit vs. Manufacturing Cost', group: 'chart' }] : []),
        ...(hasOutlets       ? [{ id: 'outlet-sales',   label: 'Outlet-wise Sales',                group: 'table' }] : []),
    ];
}

/**
 * The /reports/enhanced-export query, built with the SAME rules SalesReport uses
 * for its own /summary fetch, so both endpoints resolve the exact same period:
 * dates are formatted to YYYY-MM-DD (never raw Date objects), single-day custom
 * ranges use `date=`, and language/noCache are always sent.
 */
export function salesEnhancedExportQuery({ selectedPeriod, selectedDate, selectedWeek, businessId = '', language = 'en' } = {}) {
    let q = `?businessId=${businessId || ''}`;
    if (selectedWeek && selectedWeek.isCustomRange && selectedWeek.isSingleDay) {
        q += `&date=${toYMD(selectedWeek.start)}`;
    } else if (selectedWeek) {
        q += `&weekStart=${toYMD(selectedWeek.start)}&weekEnd=${toYMD(selectedWeek.end)}`;
    } else if (selectedDate && selectedDate.isMonthSelection) {
        q += `&period=month&monthYear=${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}`;
    } else if (selectedDate) {
        q += `&date=${toYMD(selectedDate)}`;
    } else {
        const periodParam = { Today: 'today', 'This Week': 'week', 'This Month': 'month', 'This Year': 'year' }[selectedPeriod];
        if (periodParam) q += `&period=${periodParam}`;
    }
    return `${q}&language=${language}&noCache=1`;
}

/**
 * Financial truth the export shares with the screen: the masking, the footnotes
 * that travel with the file, the headline, and the profit/cost split.
 */
export function salesReportFigures(summary = {}) {
    // A2: "-" is reserved for a period where NOTHING could be costed. A period with
    // some unpriced dishes exports the real subtotal of the rest, plus the exclusion
    // footnote — the same thing the screen shows. The `??` fallback preserves the old
    // behaviour for responses cached under the previous shape.
    const cogsUnavailable = Boolean(summary.all_cogs_unavailable ?? summary.has_unavailable_cogs_items);
    // Dishes held out of every figure. An accountant reading a total that silently
    // omits two dishes has been misled; one that names them has not.
    const excludedCogsCount = Number(summary.excluded_cogs_item_count || 0);
    const excludedCogsRevenue = Number(summary.excluded_cogs_revenue || 0);
    const excludedCogsNames = (summary.excluded_cogs_items || [])
        .map((i) => i && i.item_name).filter(Boolean);
    const excludedFootnote = (!cogsUnavailable && excludedCogsCount > 0)
        ? `Excludes ${excludedCogsCount} sold item(s) with no price history (never purchased, never counted), `
          + `representing ₹${excludedCogsRevenue.toFixed(2)} of sales. Both their revenue and their cost are held out `
          + `of every profit and cost figure in this report`
          + (excludedCogsNames.length ? `: ${excludedCogsNames.join(', ')}.` : '.')
        : null;
    // Estimated (not unavailable) COGS: the figures ARE exported, but part of the cost
    // came from an earlier purchase price. An exported PDF outlives the session that
    // made it, so the footnote is the only thing that travels with it.
    const cogsEstimated = !cogsUnavailable && Boolean(summary.has_estimated_cogs_items);
    const estimatedCogsAmount = Number(summary.estimated_cogs_amount || 0);
    const estimatedCogsCount = Number(summary.estimated_cogs_item_count || 0);
    const estimatedFootnote = cogsEstimated
        ? `~ Estimated. ${estimatedCogsCount} item(s) include ingredients priced at their last known purchase price `
          + `because stock was unavailable and no confirmed closing-stock price exists. `
          + `Estimated cost included: ₹${estimatedCogsAmount.toFixed(2)} of ₹${Number(summary.total_cogs || 0).toFixed(2)} total cost.`
        : null;
    // Headline profit = the exact value the on-screen "Gross Profit" card shows (net
    // of wastage), via the same fallback chain.
    const headlineProfit = Number(
        summary.display_net_profit ?? summary.net_profit ?? summary.display_gross_profit ?? summary.gross_profit ?? 0
    );
    const headlineLabel = headlineProfit < 0 ? 'Net Loss' : 'Gross Profit';
    // COGS = the adjusted/display value the on-screen pie/legend uses, not the raw column.
    const displayCogs = (summary.display_total_cogs !== undefined && summary.display_total_cogs !== null)
        ? Number(summary.display_total_cogs)
        : Number(summary.total_cogs || 0);
    // The profit / cost / wastage split the on-screen donut draws (net of wastage).
    const split = profitSplit(summary);
    // Denominator for "% of net sale" on the profit/COGS sheets: the sales those
    // figures describe — Total Sales minus the revenue nothing could cost.
    const profitBasis = (summary.display_sales_basis !== undefined && summary.display_sales_basis !== null)
        ? Number(summary.display_sales_basis)
        : Number(summary.total_sales || 0);
    return {
        cogsUnavailable, excludedCogsCount, excludedCogsRevenue, excludedCogsNames, excludedFootnote,
        cogsEstimated, estimatedCogsAmount, estimatedCogsCount, estimatedFootnote,
        headlineProfit, headlineLabel, displayCogs, split, profitBasis,
    };
}

const toDayMonth = (v) => {
    const s = String(v || '');
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}` : s;
};

/**
 * The editorial PDF's data. COGS/profit honour the same "-" masking the
 * on-screen cards use when cost is unknown.
 */
/**
 * The Sales Report's Fixed Expense tab as export rows: the same groups, the same masking (a
 * viewer without salary rights gets worker names with no amounts, and totals without salaries).
 */
export function fixedExpenseRows(share) {
    if (!share || !share.available || !(share.groups || []).length) return null;
    const STATUS = { estimated: 'Estimated', confirmed: 'Confirmed', locked: 'Locked' };
    const PAY = { paid: 'Paid', part: 'Part paid', unpaid: 'Not paid', overdue: 'Overdue' };
    return {
        total: Number(share.total) || 0,
        paid: Number(share.paid) || 0,
        due: Number(share.due) || 0,
        estimated: !!share.estimated,
        excludesSalaries: !!share.excludesSalaries && share.groups.some((g) => g.key === 'salaries'),
        groups: share.groups.map((g) => ({
            name: g.name,
            masked: !!g.masked,
            total: g.masked ? null : Number(g.total) || 0,
            rows: g.cards.map((c) => ({
                name: c.role ? `${c.name} (${c.role})` : c.name,
                behavior: c.behaviorLabel || '',
                status: STATUS[c.status] || '',
                payment: c.masked ? '' : (PAY[c.paymentStatus] || ''),
                amount: c.masked ? null : Number(c.amount) || 0,
            })),
        })),
    };
}

export function buildSalesPdfData({
    summary = {}, expenses = {}, wastageExpenses = {}, topProfitItems = [], chartData = [], dailyBreakdown = [],
    selectedPeriod = '', selectedDate, selectedWeek, displayText = '', enhanced = null, preparedFor = '', generated = '',
    outletRows = [], fixedShare = null,
} = {}) {
    const periodLabel = displayText || selectedPeriod || 'Report';
    const fixed = fixedExpenseRows(fixedShare);
    const { isSingleDaySelection, isCustomMultiDayRange } = salesPeriodFlags({ selectedPeriod, selectedDate, selectedWeek });
    const { cogsUnavailable, headlineProfit, split, estimatedFootnote, excludedFootnote } = salesReportFigures(summary);

    const totalSales = Number(summary.total_sales || 0);
    // Stock-In + wastage — what the Expense card prints. Fixed expenses get their own section
    // but are not added to this figure (for now).
    const totalExpense = Number(expenses.total_expenses || 0) + Number(wastageExpenses.total_expenses || 0);
    const totalWastage = Number(summary.total_wastage || 0);

    // Top items — real gross profit (losses not hidden); share of the positive total.
    const tiRaw = (topProfitItems || []).map((i) => ({ name: i.item_name || '', gp: Number(i.gross_profit || 0) }));
    const tiTotal = tiRaw.reduce((s, i) => s + i.gp, 0);

    const ps = enhanced?.summary;
    const psRow = (scope, o) => ({ scope, revenue: Number(o?.sales || 0), expenses: Number(o?.expenses || 0), net: Number(o?.profit || 0) });
    const noComparison = isSingleDaySelection || isCustomMultiDayRange;

    return {
        meta: {
            brand: 'INVEXIS', title: 'Sales Report',
            period: periodLabel, periodShort: periodLabel,
            generated, preparedFor, generatedBy: 'Generated by Invexis',
            kicker: 'Period Performance', heroLine1: 'Sales', heroLine2: 'Report',
            subtitle: 'A full reconciliation of in-house and online orders against ingredient and operating costs for the selected period.',
        },
        summary: {
            totalSales,
            expenses: totalExpense,
            grossProfit: cogsUnavailable ? '-' : headlineProfit,
        },
        fixedExpenses: fixed,
        // The "Sales Comparison Chart" is a fixed multi-period breakdown (Mon–Sun for a
        // week, Week 1–4 for a month, months for a year). Omitted for a single day
        // (nothing to compare) AND for a custom multi-day range, which gets its own
        // Date-wise Breakdown table instead.
        salesComparison: noComparison ? [] : (chartData || []).map((d) => ({ label: d.label || '', amount: Number(d.total_sales || 0) })),
        salesComparisonTotal: noComparison ? 0 : (chartData || []).reduce((s, d) => s + Number(d.total_sales || 0), 0),
        dateBreakdown: (dailyBreakdown || []).map((r) => ({ date: toDayMonth(r.date), sales: Number(r.total_sales || 0), expense: Number(r.expense || 0), wastage: Number(r.wastage || 0) })),
        topItems: tiRaw.map((i) => ({ name: i.name, grossProfit: i.gp, share: tiTotal > 0 ? (i.gp / tiTotal * 100) : '-' })),
        profitCogs: {
            grossProfit: { amount: cogsUnavailable ? '-' : split.profit, pct: cogsUnavailable ? '-' : split.pct.profit },
            mfgCost: { amount: cogsUnavailable ? '-' : split.cogs, pct: cogsUnavailable ? '-' : split.pct.cogs },
            waste: { amount: totalWastage, pct: split.pct.waste },
            netSale: totalSales,
        },
        // Omitted for a custom range so the PDF reflects only the selected period (an
        // arbitrary range collapses the Yearly and Selected rows to the same figure).
        periodSummary: (ps && !isCustomMultiDayRange) ? [psRow('Weekly Period', ps.weekly), psRow('Monthly Period', ps.monthly), psRow('Yearly Period', ps.yearly), psRow('Selected Period (Primary)', ps.primary)] : [],
        itemsSold: (enhanced?.items_sold || []).map((i) => ({ name: i.item_name, qty: i.quantity_sold, amount: i.total_sales_amount, orders: i.number_of_orders, contribution: i.contribution_percentage })),
        stockIn: (enhanced?.stock_in || []).map((i) => ({ name: i.ingredient_name, qty: Number(i.quantity_added || 0).toLocaleString('en-IN'), unit: i.unit, cost: i.purchase_cost, supplier: i.supplier, date: i.date })),
        stockOut: (enhanced?.stock_out || []).map((i) => ({ name: i.ingredient_name, qty: Number(i.quantity_consumed || 0).toLocaleString('en-IN'), unit: i.unit, related: i.related_item, date: i.date })),
        nonChargeable: (enhanced?.non_chargeable || []).map((i) => ({ name: i.expense_name, category: i.category, amount: i.amount, date: i.date, remarks: i.remarks })),
        // Travel with the document. Null when nothing was estimated / excluded.
        estimatedCogsNote: estimatedFootnote,
        excludedCogsNote: excludedFootnote,
        // Outlet-wise Sales — present only when the period has outlet sales.
        outlets: (outletRows || []).map((r) => ({
            outlet: r.label || r.outlet || 'Regular TSR',
            labelled: !!r.outlet,
            days: Number(r.days) || 0,
            sales: Number(r.sales) || 0,
            discount: Number(r.discount) || 0,
            gross_profit: r.gross_profit == null ? null : Number(r.gross_profit),
            // ≈ — the figure includes an estimated cost for Not-in-Menu revenue.
            gp_estimated: r.gross_profit != null && !!r.gross_profit_estimated,
        })),
    };
}
